import { DateInputProps, DateValue } from "@mantine/dates";
import { DateInput as MantineDateInput } from "@mantine/dates";
import cx from "clsx";
import classes from "./index.module.css";
import dayjs from "dayjs";
import { useTranslation } from "@/i18n/client";
import { Calendar, ChevronSmallLeft, ChevronSmallRight } from "@/components/Icons";
import { useState } from "react";
import { getInputProps } from "@/components/inputProps";
import { DateFormat } from "@/constants/common";

const DecadeLabelFormat = () => {
  const { t } = useTranslation("components");
  return <div className={classes.decadeLabel}>{t("DateInput.chooseYear")}</div>;
};

const MonthLabelFormat = (date: Date) => {
  const d = dayjs(date);
  return (
    <div className={classes.monthYearLabelWrap}>
      <span>{d.format("MMMM")}</span> <span className={classes.monthYearLabel}>{d.format("YYYY")}</span>
    </div>
  );
};

// Collapse any mix of separators/whitespace into groups of digits so inputs
// like "4 8/  2001." are parsed the same as "4.8.2001".
const normalizeDateInput = (input: string) => {
  const groups = input.match(/\d+/g);
  if (!groups) {
    return input;
  }

  // Continuous 8-digit input is ambiguous (DDMMYYYY vs YYYYMMDD). Treat it as
  // year-first only when the leading 4 digits are a plausible year and the next
  // two are a valid month; otherwise fall back to day-first DDMMYYYY.
  if (groups.length === 1 && groups[0].length === 8) {
    const digits = groups[0];
    const year = Number(digits.slice(0, 4));
    const month = Number(digits.slice(4, 6));
    if (year >= 1850 && year <= 2100 && month >= 1 && month <= 12) {
      return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
    }
    return digits;
  }

  if (groups.length === 3) {
    // Year-first when the leading group looks like a 4-digit year.
    return groups[0].length === 4 ? groups.join("-") : groups.join(".");
  }

  return input;
};

const dateParser: DateInputProps["dateParser"] = input =>
  dayjs(
    normalizeDateInput(input),
    [
      "DD.MM.YYYY",
      "D.M.YYYY",
      "D. M. YYYY",
      "DD.M.YYYY",
      "D.MM.YYYY",
      "D-M-YYYY",
      "DD-M-YYYY",
      "DD-MM-YYYY",
      "DD. MM. YYYY",
      "DD/MM/YYYY",
      "DDMMYYYY",
      "YYYYMMDD",
      "YYYY-MM-DD",
      "YYYY.MM.DD",
      "YYYY/MM/DD",
      "YYYY MM DD",
      "YYYY-M-D",
      "YYYY.M.D",
      "YYYY/M/D",
      "YYYY M D",
    ],
    true
  ).toDate();

const DateInput = ({ pii = false, ...dateInputProps }: DateInputProps & { pii?: boolean }) => {
  const { i18n } = useTranslation("components");
  const [focused, setFocused] = useState(false);
  const [datePickerOpened, setDatePickerOpened] = useState(false);
  return (
    <MantineDateInput
      {...dateInputProps}
      {...getInputProps(dateInputProps, focused)}
      className={cx(dateInputProps.className, { piiData: pii })}
      onChange={(value: DateValue) => {
        setDatePickerOpened(false);
        dateInputProps.onChange?.(value);
      }}
      valueFormat={DateFormat}
      dateParser={dateParser}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      popoverProps={{ opened: datePickerOpened, closeOnClickOutside: true, onChange: opened => setDatePickerOpened(opened) }}
      rightSectionProps={{
        onClick: () => setDatePickerOpened(true),
      }}
      nextIcon={<ChevronSmallRight fontSize={32} />}
      previousIcon={<ChevronSmallLeft fontSize={32} />}
      locale={i18n.language}
      decadeLabelFormat={DecadeLabelFormat}
      monthLabelFormat={MonthLabelFormat}
      rightSectionWidth={40}
      rightSection={dateInputProps.disabled ? null : <Calendar fontSize={24} />}
      size="lg"
      defaultLevel={dateInputProps.defaultLevel ?? "decade"}
      classNames={classes}
    />
  );
};

export default DateInput;
